import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import {
  useArchiveGHADiscountScopeRule,
  useCreateGHADiscountScopeRule,
  useGHADiscountScopeRules,
  useReorderGHADiscountScopeRules,
  useReplaceGHADiscountScopeRule,
} from '~/hooks/apis/connection-google/useGHADiscountScopeRules';

import type { GHADiscountRuleAccountType, GHADiscountScopeRule } from '~/services/ConnectionGoogleService';
import { listGHADiscountScopeRules } from '~/services/ConnectionGoogleService';

import AttributionCell from './AttributionCell';
import ScopeRuleDialog from './ScopeRuleDialog';
import { ACCOUNT_LABELS, GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS } from './constants';
import { formatScopeCriteria, moveRule } from './utils';

// Priority order IS the semantics (a firewall list): enabled rules render grouped per account in
// priority order, and the up/down arrows submit the account's FULL intended ordering.
export default function ScopeRulesPanel({ focusedRuleId }: { focusedRuleId?: string | null }) {
  const [accountFilter, setAccountFilter] = useState<'' | GHADiscountRuleAccountType>('');
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<GHADiscountScopeRule | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const showConfirmDialog = useConfirmDialog();

  const { data, isLoading, isFetching, error } = useGHADiscountScopeRules({
    accountType: accountFilter === '' ? undefined : accountFilter,
    enabled: showArchived ? undefined : true,
    limit: 100,
  });
  const createMutation = useCreateGHADiscountScopeRule();
  const replaceMutation = useReplaceGHADiscountScopeRule();
  const archiveMutation = useArchiveGHADiscountScopeRule();
  const reorderMutation = useReorderGHADiscountScopeRules();

  const rules = useMemo(() => {
    const rows = [...(data?.result ?? [])];
    // Enabled first, grouped by account, priority order; archived trail by recency.
    return rows.sort(
      (a, b) =>
        Number(b.enabled) - Number(a.enabled) ||
        a.accountType.localeCompare(b.accountType) ||
        (a.enabled ? a.priority - b.priority : b.updatedAt.localeCompare(a.updatedAt)),
    );
  }, [data]);

  const nextPriority = useMemo(() => {
    const enabled = (data?.result ?? []).filter((rule) => rule.enabled);
    return enabled.length === 0 ? 1 : Math.max(...enabled.map((rule) => rule.priority)) + 1;
  }, [data]);

  // Arrows freeze while a reorder is in flight OR while the list refetches — the visible order is
  // stale in that window, and a second click would recompute from it and undo the first move.
  const moveDisabled = reordering || reorderMutation.isPending || isFetching;
  const orderBounds = useMemo(() => {
    const bounds = new Map<string, { first: boolean; last: boolean }>();
    const enabled = (data?.result ?? []).filter((rule) => rule.enabled);
    for (const accountType of new Set(enabled.map((rule) => rule.accountType))) {
      const accountRules = enabled
        .filter((rule) => rule.accountType === accountType)
        .sort((a, b) => a.priority - b.priority);
      accountRules.forEach((rule, index) => {
        bounds.set(rule.id, { first: index === 0, last: index === accountRules.length - 1 });
      });
    }
    return bounds;
  }, [data]);

  // The reorder set comes from a fresh per-account fetch, not the display query — the display can
  // be truncated by the list limit or stale between a mutation and its refetch, and the backend
  // requires the FULL active ordering.
  const handleMove = async (rule: GHADiscountScopeRule, direction: -1 | 1) => {
    setReordering(true);
    try {
      const fresh = await listGHADiscountScopeRules({
        accountType: rule.accountType,
        enabled: true,
        limit: 100,
      });
      const orderedIds = moveRule(fresh.result, rule, direction);
      if (orderedIds != null) {
        await reorderMutation.mutateAsync({ accountType: rule.accountType, orderedIds });
      }
    } catch {
      // Reorder failures snackbar via the mutation hook; a failed fresh fetch just aborts the move.
    } finally {
      setReordering(false);
    }
  };

  const handleArchive = async (rule: GHADiscountScopeRule) => {
    const confirmed = await showConfirmDialog({
      title: 'Archive scope rule?',
      description:
        'Archiving is one-way: the rule stops winning new emissions immediately, but tokens already minted ' +
        'against it keep honouring. To change the rule instead, use Edit (replace).',
    });
    if (confirmed) {
      archiveMutation.mutate(rule.id);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setRuleToEdit(null);
    setSubmitError(null);
  };

  const handleSubmit = async (input: Parameters<typeof createMutation.mutate>[0]) => {
    setSubmitError(null);
    try {
      if (ruleToEdit == null) {
        await createMutation.mutateAsync(input);
      } else {
        await replaceMutation.mutateAsync({ id: ruleToEdit.id, input });
      }
      closeDialog();
    } catch (submitException) {
      // Registration-validation 400s carry the register-and-upload instruction — show verbatim.
      setSubmitError(submitException instanceof Error ? submitException.message : 'Failed to save the rule');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">Failed to load scope rules</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="scope-rules-account-filter">Account</InputLabel>
            <Select
              labelId="scope-rules-account-filter"
              label="Account"
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
            >
              <MenuItem value="">All accounts</MenuItem>
              {GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={showArchived} onChange={(_, checked) => setShowArchived(checked)} />}
            label="Show archived"
          />
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New scope rule
        </Button>
      </Stack>

      {data != null && data.total > rules.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing {rules.length} of {data.total} rules — narrow the account filter to see the rest.
        </Alert>
      )}

      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No scope rules yet. A scope rule grants a discount to every matching property that has no property-level rule
          of its own.
        </Typography>
      ) : (
        <Table size="small" sx={{ tableLayout: 'fixed', '& td, & th': { py: 1 } }}>
          <TableHead>
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell sx={{ width: '7%' }}>Priority</TableCell>
              <TableCell sx={{ width: '8%' }}>Account</TableCell>
              <TableCell sx={{ width: '21%' }}>Scope</TableCell>
              <TableCell sx={{ width: '14%' }}>Discount</TableCell>
              <TableCell sx={{ width: '8%' }} align="center">
                ABW / LOS
              </TableCell>
              <TableCell sx={{ width: '22%' }}>Created / updated</TableCell>
              <TableCell align="center" sx={{ width: '8%' }}>
                Order
              </TableCell>
              <TableCell align="right" sx={{ width: '12%' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule) => (
              <TableRow
                key={rule.id}
                sx={{
                  ...(rule.id === focusedRuleId && { backgroundColor: 'action.selected' }),
                  ...(rule.enabled ? {} : { opacity: 0.55 }),
                }}
              >
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {rule.enabled ? rule.priority : <Chip size="small" label="Archived" />}
                </TableCell>
                <TableCell>{ACCOUNT_LABELS[rule.accountType] ?? rule.accountType}</TableCell>
                <TableCell>{formatScopeCriteria(rule)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={rule.discountType === 'margin_retention' ? 'info' : 'success'}
                    label={`${rule.discountValue}% ${rule.discountType === 'margin_retention' ? 'Margin Retention' : 'Percentage'}`}
                  />
                </TableCell>
                <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {rule.minAdvanceBookingWindow} / {rule.minLengthOfStay}
                </TableCell>
                <TableCell>
                  <AttributionCell
                    createdBy={rule.createdBy}
                    updatedBy={rule.updatedBy}
                    createdAt={rule.createdAt}
                    updatedAt={rule.updatedAt}
                  />
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {rule.enabled && (
                    <>
                      <Tooltip title="Move up (wins earlier)">
                        <span>
                          <IconButton
                            size="small"
                            disabled={moveDisabled || orderBounds.get(rule.id)?.first}
                            onClick={() => handleMove(rule, -1)}
                          >
                            <ArrowUpwardIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            disabled={moveDisabled || orderBounds.get(rule.id)?.last}
                            onClick={() => handleMove(rule, 1)}
                          >
                            <ArrowDownwardIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {rule.enabled && (
                    <>
                      <Button
                        size="small"
                        onClick={() => {
                          setRuleToEdit(rule);
                          setDialogOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button size="small" color="warning" onClick={() => handleArchive(rule)}>
                        Archive
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ScopeRuleDialog
        open={dialogOpen}
        initialRule={ruleToEdit}
        defaultPriority={nextPriority}
        isSaving={createMutation.isPending || replaceMutation.isPending}
        submitError={submitError}
        onClose={closeDialog}
        onSubmit={handleSubmit}
      />
    </Box>
  );
}
